<template>
  <div>
    <b-card bg-variant="light">
      <form @submit.prevent="updateGlobalMinVersion">
        <h3>Client min version global</h3>

        <template v-if="mayChangeClientMinVersion">
          <div class="form-group">
            <label for="new-version">Min: <b style="color:red">{{current_version}}</b>. Set new (up to {{max_version}}):</label>
            <input id="new-version" ref="minVersionInput" class="form-control" v-model="newMinVersion" required>
            <br>
            <label for="new-version-recommended">Recommended: <b style="color:orange">{{current_version_recommended}}</b>. Set new (up to {{max_version}}):</label>
            <input id="new-version-recommended" ref="recommendedVersionInput" class="form-control" v-model="newRecommendedVersion" required>
          </div>

          <b-form-checkbox
            id="checkbox-must-update-platoform-versions"
            v-model="mustUpdatePlatformVersions"
            class="mb-3"
          >
            Enforce min version for all platforms
          </b-form-checkbox>

          <b-button type="submit" variant="danger">Enforce</b-button>
        </template>

      </form>
    </b-card>

    <hr>

    <h2
      class="mb-3"
    >
      Client min version by platform
    </h2>

    <template
      v-if="clientVersionInfo !== null"
    >
      <platform-block
        v-for="platformInfo in clientVersionInfo.platforms"
        :key="platformInfo.platform"
        :versionMax="clientVersionInfo.versionMax"
        :info="platformInfo"
        v-on:platform-min-version-update-requested="updatePlatformMinVersion"
        class="platform-block"
      />
    </template>
  </div>
</template>

<script>
import auth from '../auth';
import { HTTP } from '../http-common';
import notifier from '../notifier';
import { mayChangeClientMinVersion as utilMayChangeClientMinVersion } from '../util';
import { getPlatformName } from '../util';
import { parseVersion2 } from '../util';
import { VersionMajorMinorInvalidFormatError } from '../util';
import PlatformBlock from './SystemClientMinVersionOnPlatform.vue';

export default {
  name: 'SystemClientMinVersion',

  components:{
    PlatformBlock,
  },

  data() {
    return {
      actor: auth.getViewer(),

      current_version: '0.0',
      current_version_recommended: '0.0',
      max_version: '0.0',
      newMinVersion: '0.0',
      newRecommendedVersion: '0.0',

      clientVersionInfo: null,
      mustUpdatePlatformVersions: true,
    };
  },

  computed: {
    mayChangeClientMinVersion() {
      return utilMayChangeClientMinVersion(this.actor.role);
    },
  },

  created() {
    notifier.deleteSenderNotifications(this);
    this.loadCurrentVersion();
    this.loadClientVersionInfo();
  },

  methods: {
    loadCurrentVersion() {
      HTTP.get('/min_client_version')
      .then(response => {
        this.current_version = response.data.version;
        this.max_version = response.data.version_max;
        this.newMinVersion = this.current_version;
        this.current_version_recommended = response.data.recommended_version;
        this.newRecommendedVersion = this.current_version_recommended;
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    loadClientVersionInfo() {
      const url = '/client_version_info';
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.clientVersionInfo = data;
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    updateGlobalMinVersion() {
      notifier.deleteSenderNotifications(this);

      var trimmedNewMinVersion = this.newMinVersion.trim();
      var trimmedNewRecommendedVersion = this.newRecommendedVersion.trim();

      const minVersionReport = parseVersion2(trimmedNewMinVersion);
      if(minVersionReport.error !== null) {
        this.outputGlobalMinVersionValidationError(minVersionReport.error, "min");
        return;
      }

      const recommendedVersionReport = parseVersion2(trimmedNewRecommendedVersion);
      if(recommendedVersionReport.error !== null) {
        this.outputGlobalMinVersionValidationError(recommendedVersionReport.error, "recommended");
        return;
      }

      const v = minVersionReport.version
      const vr = recommendedVersionReport.version

      //Recommended version can't be lower than a minimal one. 
      //Setting it to a minimal one automatically if needed.
      if(v.major > vr.major || (v.major === vr.major && v.minor > vr.minor)) {
        trimmedNewRecommendedVersion = trimmedNewMinVersion;
        this.current_version_recommended = this.current_version;
      }

      const data = new FormData();
      data.set('new_version', trimmedNewMinVersion);
      data.set('new_recommended_version', trimmedNewRecommendedVersion);

      const mustUpdatePlatformVersions = this.mustUpdatePlatformVersions;
      if(mustUpdatePlatformVersions) {
        data.set('platforms_update_required', true);
      }

      HTTP.post('/min_client_version', data)
      .then(() => {
        notifier.showSuccess('Client min version enforced.');

        if(mustUpdatePlatformVersions) {
          this.setMinVersionForAllPlatforms(trimmedNewMinVersion, trimmedNewRecommendedVersion);
        }

        this.loadCurrentVersion();
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    outputGlobalMinVersionValidationError(error, tag) {
      this.$refs.minVersionInput.focus();

      let message = '';
      if(error instanceof VersionMajorMinorInvalidFormatError) {
        message = 'Can not enforce client global '+tag+' version. Min version is specified in invalid format. Valid format is "Major.Minor".';
      } else {
        message = 'Can not enforce client global '+tag+' version. ' + error.message;
      }
      notifier.showError(message, {sender: this});
    },

    updatePlatformMinVersion(platform, newMinVersion, newRecommendedVersion, platformView) {
      notifier.deleteSenderNotifications(this);

      var trimmedNewMinVersion = newMinVersion.trim();
      var trimmedNewRecommendedVersion = newRecommendedVersion.trim();

      const minVersionReport = parseVersion2(trimmedNewMinVersion);
      if(minVersionReport.error !== null) {
        this.outputPlatformMinVersionValidationError(platform, platformView, minVersionReport.error);
        return;
      }

      const recommendedVersionReport = parseVersion2(trimmedNewRecommendedVersion);
      if(recommendedVersionReport.error !== null) {
        this.outputPlatformMinVersionValidationError(platform, platformView, recommendedVersionReport.error);
        return;
      }

      const v = minVersionReport.version
      const vr = recommendedVersionReport.version

      //Recommended version can't be lower than a minimal one. 
      //Setting it to a minimal one automatically if needed.
      if(v.major > vr.major || (v.major === vr.major && v.minor > vr.minor)) {
        trimmedNewRecommendedVersion = trimmedNewMinVersion;
      }

      const data = new FormData();
      data.append('new_version', trimmedNewMinVersion);
      data.append('new_recommended_version', trimmedNewRecommendedVersion);

      const url = '/min_client_version/platform/' + platform;
      HTTP.post(url, data)
      .then(() => {
        const platformInfo = this.findPlatformInfo(platform);
        if(platformInfo !== null) {
          platformInfo.minVersion = trimmedNewMinVersion;
          platformInfo.recommendedVersion = trimmedNewRecommendedVersion;
        }
        const platformName = getPlatformName(platform);
        notifier.showSuccess(`Client min version for ${platformName} enforced.`);
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    outputPlatformMinVersionValidationError(platform, platformView, error) {
      platformView.focus();

      const platformName = getPlatformName(platform);

      let message = `Can not enforce client min version for ${platformName}.`;
      if(error instanceof VersionMajorMinorInvalidFormatError) {
        message += ' Min version is specified in invalid format. Valid format is "Major.Minor".';
      } else {
        message += ' ' + error.message;
      }

      notifier.showError(message, {sender: this});
    },

    findPlatformInfo(platform) {
      for(const platformInfo of this.clientVersionInfo.platforms) {
        if(platformInfo.platform === platform) {
          return platformInfo;
        }
      }
      return null;
    },

    setMinVersionForAllPlatforms(newMinVersion, newRecommendedVersion) {
      if(this.clientVersionInfo === null) {
        return;
      }

      for(const platformInfo of this.clientVersionInfo.platforms) {
        platformInfo.minVersion = newMinVersion;
        platformInfo.recommendedVersion = newRecommendedVersion;
      }
    }
  },

};
</script>

<style scoped>
  .platform-block {
    margin-bottom: 1.5rem;
  }
</style>
